// Property → Overview (/app/property): the demo's Property Management across every development in the user's scope —
// every reported job and every certificate, worst first. One call, GET /property/overview, which reads each development
// through its own Property management tab. The stat row doubles as filters (?filter=): the table and "Open work" narrow
// to what is behind the number. Arrears show here only to a role that can read them, and link to Finance → Arrears.
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { useCan } from '../../hooks/usePermission';
import { useStatFilter } from '../../hooks/useStatFilter';
import { StatTile } from '../charts/StatTile';
import { PageHeader } from '../ui';
import { formatDate } from '../../lib/viz';
import { PRIORITY_COLOUR, humanise } from '../../lib/propertyManagement';
import { propertyOverview, type DevelopmentProperty, type OpenJob, moneyTile, tileRow } from '../../lib/portfolio';
import { CertChip, Count, FootLink, Quiet, SectionHead, ShowAll, Th } from './bits';

type Filter = 'open' | 'overdue' | 'emergency' | 'certsx' | 'certsd' | 'wo';
const JOBS_SHOWN = 16;
const DEV: Partial<Record<Filter, (d: DevelopmentProperty) => boolean>> = {
  open: (d) => (d.open ?? 0) > 0, overdue: (d) => (d.overdue ?? 0) > 0, emergency: (d) => (d.emergency ?? 0) > 0,
  certsx: (d) => (d.certs?.expired ?? 0) > 0, certsd: (d) => (d.certs?.due ?? 0) > 0,
};
const JOB: Partial<Record<Filter, (j: OpenJob) => boolean>> = {
  overdue: (j) => j.overdue, emergency: (j) => j.priority === 'EMERGENCY', wo: (j) => !!j.vendorName,
};
const daysFrom = (iso: string) => Math.round((Date.parse(iso) - Date.now()) / 86_400_000);

export function PropertyOverviewTab() {
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ['/property/overview'], queryFn: propertyOverview });
  const tf = useStatFilter<Filter>();
  const canBills = useCan('bills.read');
  const [allJobs, setAllJobs] = useState(false);
  const o = q.data;

  const header = <PageHeader title="Property management" />;
  if (q.isLoading) return <>{header}<div className="grid gap-2.5" style={tileRow}>{Array.from({ length: 8 }, (_, i) => <div key={i} className="surface h-[78px] animate-pulse" />)}</div></>;
  if (!o) return <>{header}<div className="py-12 text-center text-sm text-ink-400">{q.error ? (q.error as Error).message : 'Nothing to show'}</div></>;

  const s = o.stats; const v = o.visible;
  const f = tf.active;
  const rows = o.developments.filter((d) => !f || !DEV[f] || DEV[f]!(d));
  const jobs = (o.openWork ?? []).filter((j) => !f || !JOB[f] || JOB[f]!(j));
  const shownJobs = allJobs ? jobs : jobs.slice(0, JOBS_SHOWN);
  const spend = moneyTile(s.worksSpend);
  const unpaid = moneyTile(s.invoicesUnpaid?.owed, '£0');
  const arrears = moneyTile(o.arrears?.owed, '£0');
  const more = (s.openWorks ?? 0) - (o.openWork?.length ?? 0);

  return (
    <div className="space-y-2.5">
      {header}
      <div className="grid gap-2.5" style={tileRow}>
        {s.openWorks !== null && <StatTile compact label="Open works" value={s.openWorks} hint={`${o.developments.length} developments`} {...tf.tile('open')} />}
        {s.overdue !== null && <StatTile compact label="Overdue" value={s.overdue} hint="past target" status={s.overdue ? 'critical' : undefined} {...tf.tile('overdue')} />}
        {s.emergency !== null && <StatTile compact label="Emergency" value={s.emergency} status={s.emergency ? 'critical' : undefined} {...tf.tile('emergency')} />}
        {s.certsExpired !== null && <StatTile compact label="Certificates expired" value={s.certsExpired} status={s.certsExpired ? 'critical' : undefined} {...tf.tile('certsx')} />}
        {s.renewalsDue !== null && <StatTile compact label="Renewals due" value={s.renewalsDue} hint="certificates" status={s.renewalsDue ? 'warning' : undefined} {...tf.tile('certsd')} />}
        {s.worksSpend !== null && <StatTile compact label="Works spend" value={spend.value} hint={spend.hint ?? 'this year, actual cost'} />}
        {s.workOrdersOpen !== null && <StatTile compact label="Work orders open" value={s.workOrdersOpen} hint="with a contractor" {...tf.tile('wo')} />}
        {s.invoicesUnpaid !== null && <StatTile compact label="Invoices unpaid" value={unpaid.value} hint={unpaid.hint ?? `${s.invoicesUnpaid.count} contractor bill${s.invoicesUnpaid.count === 1 ? '' : 's'}`}
          status={s.invoicesUnpaid.count ? 'warning' : undefined} onClick={canBills ? () => navigate('/app/bills?filter=unpaid') : undefined} />}
      </div>

      <div className={`grid gap-2.5 ${o.openWork ? 'lg:grid-cols-[minmax(0,1fr)_minmax(0,420px)]' : ''}`}>
        <div className="min-w-0 space-y-2.5">
          <section className="surface overflow-hidden">
            <SectionHead title="By development" note={f && DEV[f] ? `${rows.length} of ${o.developments.length} — filtered` : undefined} />
            {!rows.length ? <Quiet>No development has any.</Quiet> : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px] text-[12.5px]">
                  <thead>
                    <tr className="bg-ink-50/60 text-[11.5px] font-medium uppercase tracking-wide text-ink-500">
                      <th className="px-3 py-1.5 text-left">Development</th>
                      {v.works && <><Th on={f === 'open'}>Open</Th><Th on={f === 'overdue'}>Overdue</Th><Th>Completed</Th></>}
                      {v.certificates && <Th left on={f === 'certsx' || f === 'certsd'}>Certificates</Th>}
                      {v.visits && <Th>Visits due</Th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-100">
                    {rows.map((d) => (
                      <tr key={d.propertyId} className="hover:bg-ink-50/60">
                        <td className="px-3 py-2">
                          <Link to={`/app/properties/${d.propertyId}?tab=management`} className="font-medium text-ink-900 hover:text-bronze-700 hover:underline">{d.name}</Link>
                          {d.city && <div className="text-[11px] text-ink-400">{d.city}</div>}
                        </td>
                        {v.works && <>
                          <td className="px-3 py-1.5 text-right tabular-nums"><Count n={d.open} /></td>
                          <td className="px-3 py-1.5 text-right tabular-nums"><Count n={d.overdue} tone="critical" /></td>
                          <td className="px-3 py-1.5 text-right tabular-nums"><Count n={d.completed} /></td>
                        </>}
                        {v.certificates && <td className="whitespace-nowrap px-3 py-1.5"><CertChip c={d.certs} /></td>}
                        {v.visits && <td className="px-3 py-1.5 text-right tabular-nums"><Count n={d.visitsDue} /></td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {o.arrears && (
            <section className="surface overflow-hidden">
              <SectionHead title="Arrears" note={`${o.arrears.count} tenanc${o.arrears.count === 1 ? 'y' : 'ies'}`} />
              <div className="flex items-baseline gap-3 px-4 py-3">
                <span className="text-[20px] font-semibold tabular-nums text-ink-900">{arrears.value}</span>
                <span className="text-[12px] text-ink-500">{arrears.hint ? `${arrears.hint} · ` : ''}owed across the portfolio</span>
              </div>
              <FootLink to="/app/arrears">Open the arrears ladder</FootLink>
            </section>
          )}
        </div>

        {o.openWork && (
          <aside className="min-w-0">
            <section className="surface overflow-hidden">
              <SectionHead title="Open work, worst first" note={f && JOB[f] ? `${jobs.length} — filtered` : String(s.openWorks ?? jobs.length)} />
              {!jobs.length ? <Quiet>{f ? 'Nothing here.' : 'No open work anywhere.'}</Quiet> : (
                <ul className="divide-y divide-ink-100">
                  {shownJobs.map((j) => {
                    const d = j.resolveBy ? daysFrom(j.resolveBy) : null;
                    return (
                      <li key={j.id}>
                        <Link to={`/app/work-orders/${j.id}`} className="flex items-center gap-3 px-4 py-2.5 hover:bg-ink-50/60"
                          style={{ boxShadow: `inset 3px 0 0 ${j.overdue ? 'var(--status-critical)' : PRIORITY_COLOUR[j.priority] ?? 'var(--status-neutral)'}` }}>
                          <span className="w-[58px] shrink-0">
                            <span className="block truncate font-mono text-[12px] font-semibold text-ink-900">{j.unitReference ?? 'Comm.'}</span>
                            <span className="block truncate font-mono text-[10.5px] text-ink-400">{j.reference}</span>
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-1.5">
                              <span className="truncate text-[12.5px] font-medium text-ink-800" title={j.title}>{j.title}</span>
                              {(j.priority === 'EMERGENCY' || j.priority === 'URGENT') && <span className="shrink-0 rounded-full px-1.5 py-[1px] text-[10px] font-semibold" style={{ color: PRIORITY_COLOUR[j.priority], background: 'color-mix(in srgb, currentColor 10%, transparent)' }}>{humanise(j.priority)}</span>}
                            </span>
                            <span className="block truncate text-[11.5px] text-ink-400">{j.propertyName}{j.category ? ` · ${humanise(j.category)}` : ''}</span>
                          </span>
                          <span className="shrink-0 text-right text-[11px]">
                            <span className={`block tabular-nums ${j.overdue ? 'font-semibold text-red-600' : 'text-ink-500'}`}>{d === null ? 'no target' : j.overdue ? `Overdue ${Math.max(1, -d)}d` : `Due ${formatDate(j.resolveBy!)}`}</span>
                            <span className="block text-ink-400">{humanise(j.status)}</span>
                          </span>
                          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-ink-300" />
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
              {jobs.length > JOBS_SHOWN && <ShowAll all={allJobs} total={jobs.length} onToggle={() => setAllJobs((x) => !x)} />}
              {(allJobs || jobs.length <= JOBS_SHOWN) && more > 0 && !f && <FootLink to="/app/property?sub=works&filter=open">{`${more} more on Works`}</FootLink>}
            </section>
          </aside>
        )}
      </div>
    </div>
  );
}
